import { useEffect, useLayoutEffect, useState } from 'react'
import { useStoreReducer, stateRef, authStateRef } from './store'
import { updateStateProps } from './actions'
import reducer from './reducer'
import {
  PLATFORM_APP_ID,
  TSG_NONE,
  LG_BASE_FONT_SIZE,
  ACTIVATION_ERROR_HELP_TEXT,
  ACTIVATION_ERROR_SCREEN_TITLE,
  ACTIVATION_ERROR_NO_MANIFEST_HELP_TEXT
} from './constants'
import { keyBy } from 'lodash'
import ActivationPage from './components'
import { LoadingPlaceholder, actions, indexTsgInstances } from '@sparky/framework'
import manifests from './manifests'
import { ACTIVATION_UI_ERROR_CODES, ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP } from './ErrorCodes'
import SubscriptionService from './services/SubscriptionService'
import DefaultErrorScreen from './components/DefaultErrorScreen'
import { findMostParent, findAccordionComponents, isMagicLinkHashFullyActivated, runtimeManifestOverride } from './components/common/utils'
import type { VFC } from 'react'
import type { AppManifests, SupportAccount, AuthState } from '@sparky/framework'
import type { CommonStoreState, IndexedSupportAccounts, PlatformRegions, PlatformRegion, StateRef, Licenses, License } from './store'
import { APP_ID_LOGGING_SERVICE, APP_ID_PRISMA_ACCESS, APP_ID_SDWAN } from './components/common/GlobalConstants'
import { isVMFlexActivationEnabled } from '../common/utils/Utils'
import CommonDrawerContextProviders from '../common/context/CommonDrawerContexts'


const getIndexedPlatformRegions = (apps?: AppManifests) => {
  const platformRegions: PlatformRegion[] = apps?.get(PLATFORM_APP_ID)?.regions || []
  const regionMap = new Map<string, PlatformRegion>(platformRegions.map(r => [r.name, r]))
  Object.defineProperties(Array.from(platformRegions), {
    get: { value: regionMap.get.bind(regionMap) },
    apps: { value: apps },
  })
  return platformRegions as PlatformRegions
}
const getIndexedSupportAccounts = (supportAccountIds?: SupportAccount[]) => {
  const accounts = Array.from(supportAccountIds || []) // clone
  const accountMap = new Map<string | number, SupportAccount>()
  for (const acc of accounts) {
    accountMap.set(+acc.accountid, acc)
    accountMap.set(String(acc.accountid), acc)
  }
  Object.defineProperties(accounts, {
    get: {
      value: accountMap.get.bind(accountMap)
    },
    isLoaded: {
      value: Array.isArray(supportAccountIds)
    },
    supportAccountIds: { value: supportAccountIds },
  })
  return accounts as IndexedSupportAccounts
}
const getIndexedLicenses = (licenses?: License[]) => {
  const licenseArray = Array.from(licenses || [])
  const licensesMap = new Map<string, License>(licenseArray?.map(l => [l.app_id, l]))
  Object.defineProperties(licenseArray, {
    get: { value: licensesMap.get.bind(licensesMap) }
  })
  return licenseArray as Licenses
}

const EMPTY_STATE = {
  manifest: undefined,
  hash: undefined,
  context: undefined,
  screen: undefined,
  licenses: undefined,
  lockedInfo: undefined,
  loadingTsg: false,
  loadingHashes: false,
  isHashFullyActivated: false,
}

const DEFAULT_EMPTY_INSTANCES = indexTsgInstances([], '')
const getUpdatedState = (auth: AuthState, stateRef?: StateRef): CommonStoreState => {
  const { tsg_id, instances: globalInstances, apps, user, tsgs } = auth
  const selectedTsgId = stateRef?.current.tsg?.id
  const tsg = (selectedTsgId === TSG_NONE.id && TSG_NONE) ||
    (selectedTsgId && tsgs.get(selectedTsgId)) || undefined
  const currentPlatformRegions = stateRef?.current.platformRegions
  const platformRegions = apps === (currentPlatformRegions as any)?.apps ? currentPlatformRegions : getIndexedPlatformRegions(apps)
  const currentSupportAccounts = stateRef?.current.supportAccounts
  const userSupportAccount = user?.supportAccountIds
  const supportAccounts = userSupportAccount === (currentSupportAccounts as any)?.supportAccountIds ? currentSupportAccounts : getIndexedSupportAccounts(user?.supportAccountIds)
  const loadingTsg = Boolean(tsg && tsg_id !== selectedTsgId)
  const instances = !selectedTsgId ? DEFAULT_EMPTY_INSTANCES : tsg_id === selectedTsgId ? globalInstances : (
    stateRef?.current.instances || indexTsgInstances([], selectedTsgId)
  )
  return {
    globalTsgId: tsg_id,
    tsg,
    tsgs,
    instances,
    apps,
    supportAccounts,
    platformRegions,
    loadingTsg,
    loading: !(apps?.length && (supportAccounts as any)?.isLoaded),
  } as CommonStoreState
}

const ActivationContainer: VFC<{ auth: AuthState, location: { pathname: string, search?: string } }> = ({ auth, location }) => {
  const state = useStoreReducer(reducer, {}, () => {
    return getUpdatedState(auth)
  })
  const [licenseLoading, setLicenseLoading] = useState<boolean>(true)
  useLayoutEffect(() => { // TODO: hacks
    const htmlStyle = document.documentElement.style
    const bodyFontSize = htmlStyle.fontSize
    if (bodyFontSize === LG_BASE_FONT_SIZE) {
      return
    }
    htmlStyle.fontSize = LG_BASE_FONT_SIZE
    return () => {
      htmlStyle.fontSize = bodyFontSize
    }
  }, [])

  useEffect(() => {
    const newState = getUpdatedState(auth, stateRef)
    if (!newState.loading) {
      updateStateProps(newState)
    }
  }, [auth])

  const selectedTsgId = state.tsg?.id
  useEffect(() => {
    const loadTsgInstances = async () => {
      if (!selectedTsgId) {
        return updateStateProps({
          loadingTsg: false,
          instances: DEFAULT_EMPTY_INSTANCES,
          absoluteRootTsg: undefined,
        })
      }

      // find most parent, and update absoluteRootTsg info
      const parentTsg = findMostParent(state.tsgs.get(selectedTsgId), state.tsgs)
      const sameTsgAsGlobal = selectedTsgId === state.globalTsgId
      updateStateProps({
        loadingTsg: !sameTsgAsGlobal,
        instances: sameTsgAsGlobal ?
          authStateRef.current!.instances :
          indexTsgInstances([], selectedTsgId || ''),
      })

      // even for same as globalTsgId, still load lastest
      const selectedTsgInstances = await actions.loadTsgInstances(selectedTsgId)
      if (stateRef.current.tsg?.id === selectedTsgInstances.tsg_id) {
        updateStateProps({
          loadingTsg: false,
          instances: selectedTsgInstances,
        })
      }
      if (parentTsg && parentTsg.isAbsoluteRoot) {
        if (parentTsg.id === selectedTsgId) {
          // same as current, no need to make call
          updateStateProps({
            absoluteRootTsg: {
              tsgId: parentTsg.id,
              instances: selectedTsgInstances,
            }
          })
        }
        else {
          // different that current, need to make call
          const parentTsgInstances = await actions.loadTsgInstances(parentTsg.id)
          updateStateProps({
            absoluteRootTsg: {
              tsgId: parentTsg.id,
              instances: parentTsgInstances,
            }
          })
        }
      }
      else {
        // the most parent tsg is not absolute root
        updateStateProps({
          absoluteRootTsg: {
            tsgId: undefined,
            instances: DEFAULT_EMPTY_INSTANCES,
            error: `cannot get absolute root tsg for tsg_id: ${selectedTsgId}`,
          }
        })
      }
    }
    loadTsgInstances()
  }, [selectedTsgId, state.globalTsgId, state.tsgs])

  const pathname = location?.pathname

  useEffect(() => {
    // const [, context, hash] = pathname.slice(1).split('/', 3)
    let context: string
    let hash: string | undefined
    let license_id: string | undefined
    let deployment_profile_id: string | undefined
    const paths = pathname.slice(1).split('/', 4)

    if (paths[1] === 'licenses') {
      // this will be activation based on license_id rather than hash
      context = paths[2]
      license_id = paths[3]
    }
    else if (paths[1] === 'profile') {
      // this will be activation based on license_id rather than hash
      const isSupportAccountEnables = isVMFlexActivationEnabled(auth?.user?.supportAccountIds)
      if (!isSupportAccountEnables) {
        updateStateProps(EMPTY_STATE) // TODO: show error message
        return
      }
      context = paths[1]
      deployment_profile_id = paths[2]
    }
    else {
      context = paths[1]
      hash = paths[2]
    }
    if (!context) {
      updateStateProps(EMPTY_STATE) // TODO: show error message
      return
    }
    const manifest = manifests[context]
    if (manifest?.flags?.hash_required && !hash && !license_id) {
      updateStateProps(EMPTY_STATE) // TODO: show error message
      return
    }
    if (manifest) {
      const screen = manifest.activation_screens[0]
      const activationSteps = keyBy(findAccordionComponents(manifest.activation_screens[0]).map((each, i) => {
        return {
          key: each.key,
          index: i,
          // only enable for index 0, others disable
          isAccordionEnabled: !Boolean(i),
          isDone: false,
          dependencies: Array.from({ length: i }, (_, index) => index)
        }
      }), 'key')
      updateStateProps({
        manifest,
        hash,
        context,
        screen,
        licenses: undefined,
        lockedInfo: undefined,
        loadingHashes: true,
        isHashFullyActivated: false,
        activationSteps,
      })
      const tsg_id = state.globalTsgId

      if (hash && tsg_id) {
        let v
        if (context === APP_ID_PRISMA_ACCESS) {
          v = 'v2'
        }
        SubscriptionService.hashes.getOne({ tsg_id, hash, v }).then(({ data }) => {
          setLicenseLoading(false)
          const licenses = getIndexedLicenses(data?.licenses)
          updateStateProps({
            manifest: runtimeManifestOverride({ manifest, licenses, context }),
            licenses,
            lockedInfo: data?.locked_info,
            loadingHashes: false,
            hashDecodeError: undefined,
            isHashFullyActivated: isMagicLinkHashFullyActivated(data),
          })
        })
          .catch((e) => {
            setLicenseLoading(false)
            updateStateProps({ ...EMPTY_STATE, hashDecodeError: e })
          })
      }
      else if (license_id) {
        const queryString = location?.search
        const urlParamas = new URLSearchParams(queryString)
        const lockedTsgIds = urlParamas.get('locked_tsg_ids')?.split(',')
        let params: { link_app_ids: string } | undefined
        switch (context) {
          case APP_ID_PRISMA_ACCESS:
            params = { link_app_ids: [APP_ID_LOGGING_SERVICE].join(',') }
            break
          default:
            params = undefined
            break
        }

        SubscriptionService.licenses
          .getOne({ tsg_id, license_id, params })
          .then(({ data }) => {
            setLicenseLoading(false)
            const licenses = getIndexedLicenses(data?.licenses)
            updateStateProps({
              manifest: runtimeManifestOverride({ manifest, licenses, context }),
              licenses,
              lockedInfo: {
                ...data?.locked_info,
                tsg_ids: lockedTsgIds,
              },
              loadingHashes: false,
              hashDecodeError: undefined,
              isHashFullyActivated: isMagicLinkHashFullyActivated(data)
            })
          })
          .catch((e) => {
            setLicenseLoading(false)
            updateStateProps({ ...EMPTY_STATE, hashDecodeError: e })
          })
      }
      else if (deployment_profile_id) {
        // updateStateProps(EMPTY_STATE)
        SubscriptionService.deploymentProfiles
          .getDP(tsg_id, deployment_profile_id)
          .then(({ data }) => {
            setLicenseLoading(false)
            //console.log('DP data', data)
            if (Boolean(data)) {
              const tsg_ids = []
              const support_account_ids = []
              const regions = []
              tsg_ids.push(`${data[0]?.tsg_id}`)
              support_account_ids.push(`${data[0]?.support_account_id}`)
              regions.push(`${data[0]?.region}`)
              const locked_info = {
                tsg_ids,
                support_account_ids,
                regions
              }
              updateStateProps({
                lockedInfo: locked_info,
                loadingHashes: false,
                hashDecodeError: undefined,
                isHashFullyActivated: false
              })
            }

          }).catch((e) => {
            setLicenseLoading(false)
            updateStateProps({
              lockedInfo: undefined,
              loadingHashes: false,
              hashDecodeError: undefined,
              isHashFullyActivated: false
            })
            //updateStateProps({ ...EMPTY_STATE, hashDecodeError: e })
          })
      }
    }
    else {
      updateStateProps(EMPTY_STATE)
    }
    setLicenseLoading(false)
  }, [auth?.user?.supportAccountIds, location?.search, pathname, state.globalTsgId])


  if (state.loading) {
    return <LoadingPlaceholder />
  }
  if (state.hashDecodeError) {
    const errorMessage = state.hashDecodeError?.msg || state.hashDecodeError?.message || ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.GET_HASHES_FAILED]
    const errorScreenProps = {
      errorMessage,
      title: ACTIVATION_ERROR_SCREEN_TITLE,
      helperMessage: ACTIVATION_ERROR_HELP_TEXT,
      linkName: 'Learn More',
      linkSrc: '#'
    }
    return <DefaultErrorScreen {...errorScreenProps} />
  }
  if (!state.manifest) {
    const errorScreenProps = {
      errorMessage: 'No manifest matched',
      title: ACTIVATION_ERROR_SCREEN_TITLE,
      helperMessage: ACTIVATION_ERROR_NO_MANIFEST_HELP_TEXT,
      linkName: 'Learn More',
      linkSrc: '#'
    }
    return <DefaultErrorScreen {...errorScreenProps} />
  }
  // disable /activation route for prisma_access and cgx on prod, remove once GA
  const DISABLE_APP_ID = new Set([APP_ID_PRISMA_ACCESS, APP_ID_SDWAN])
  if (DISABLE_APP_ID.has(state.manifest.activation_id)) {
    const appManifest = state.apps.get(state.manifest.activation_id)
    if (appManifest?.flags?.no_unified_activation) {
      const errorScreenProps = {
        errorMessage: 'No manifest matched',
        title: ACTIVATION_ERROR_SCREEN_TITLE,
        helperMessage: ACTIVATION_ERROR_NO_MANIFEST_HELP_TEXT,
        linkName: 'Learn More',
        linkSrc: '#'
      }
      return <DefaultErrorScreen {...errorScreenProps} />
    }
  }
  return (
    licenseLoading ?
      <LoadingPlaceholder /> :
      <CommonDrawerContextProviders>
        <ActivationPage state={state} />
      </CommonDrawerContextProviders>
  )
}

export default ActivationContainer
